import { put, all, takeLatest, call, select, takeEvery } from 'redux-saga/effects';
import { DataState } from '@shared/enums/DataState';
import {
  getAllLocationsData,
  getMyPeopleEvalData,
  setMyPeopleEvalDataLoading,
  setSelectedEvalsAction,
  setManagersIds,
  setMembersIds,
} from './actions';
import { MyTeamEvalData } from '@modules/EvaluationModule/interfaces/MyTeamEvalData';
import { fetchMyPeopleEvalData, MyPeopleEvalDataResponse } from '@modules/EvaluationModule/api/myPeopleEvalData';
import {
  setAllLocationsData,
  setAllLocationsDataLoading,
  setMyPeopleEvalData,
  setMyPeopleEvalTotal,
  setManagersData,
  setManagersLoading,
  setMembersData,
  setMembersLoading,
} from './reducers';
import { AsyncData } from '@shared/interfaces/asyncData';
import { selectMyPeopleEvalData } from './selectors';
import { startSelectedEvals } from '@modules/EvaluationModule/api/startSelectedEvals';
import { displayNotification } from '@modules/App/redux/notifications/actions';
import { EvaluationStatus } from '@modules/EvaluationModule/enums/EvaluationStatus';
import { setStartingEvalId } from '../../MyTeamEvalPage/redux/actions';
import { fetchLegalCountriesData } from '@shared/api/locations';
import { findLocationArea } from '@shared/helpers/findLocationArea';
import { LOCATION_AREA_ENUM } from '@shared/enums/LocationAreas';
import { fetchUserProfilePartialData } from '@modules/App/api/userProfileData';
import { UserInfo } from '@shared/interfaces/UserInfo';

function* getAllLocationsDataAsync() {
  const setLoadingAction = setAllLocationsDataLoading();
  yield put(setLoadingAction);
  try {
    const response: string[] = yield call(fetchLegalCountriesData);
    const formatCountryWithGroups = response.map((c) => ({
      name: c,
      value: c,
      isLocationArea: false,
      locationArea: findLocationArea(c),
    }));

    Object.values(LOCATION_AREA_ENUM).forEach((area) =>
      formatCountryWithGroups.push({ name: area, value: area, isLocationArea: true, locationArea: area })
    );

    const action = setAllLocationsData({ data: formatCountryWithGroups, state: DataState.Fulfilled });
    yield put(action);
  } catch {
    yield put(
      setAllLocationsData({
        data: null,
        state: DataState.Rejected,
      })
    );
  }
}

function* getMyPeopleEvalDataAsync({ payload }: ReturnType<typeof getMyPeopleEvalData>) {
  if (payload.offset === 0) {
    const setLoadingAction = setMyPeopleEvalDataLoading();
    yield put(setLoadingAction);
  }

  try {
    const myPeopleEvalData: AsyncData<MyTeamEvalData[]> = yield select(selectMyPeopleEvalData);
    const response: MyPeopleEvalDataResponse = yield call(fetchMyPeopleEvalData, payload);
    const data = myPeopleEvalData.data && response.data ? [...myPeopleEvalData.data, ...response.data] : response.data;
    yield put(setMyPeopleEvalTotal(response.total));
    const setMyPeopleEvalDataAction = setMyPeopleEvalData({
      data: data,
      state: DataState.Fulfilled,
    });
    yield put(setMyPeopleEvalDataAction);
  } catch {
    yield put(
      setMyPeopleEvalData({
        data: null,
        state: DataState.Rejected,
      })
    );
  }
}

function* setSelectedEvalsAsync({ payload }: ReturnType<typeof setSelectedEvalsAction>) {
  const evalIdToStart = payload.evaluationId;
  try {
    yield put(setStartingEvalId(evalIdToStart));
    yield call(startSelectedEvals, payload);
    const myPeopleEvalData: MyTeamEvalData[] = yield select(
      (state) => state.evaluation.myPeopleEvalPage.myPeopleEvalData.data
    );
    const newMyTeamEvalData = myPeopleEvalData.map((myTeamEval) => ({
      ...myTeamEval,
      status: evalIdToStart.includes(myTeamEval.evaluationId) ? EvaluationStatus.Ongoing : myTeamEval.status,
    }));

    const setMyTeamEvalDataAction = setMyPeopleEvalData({
      data: newMyTeamEvalData,
      state: DataState.Fulfilled,
    });
    const startedEval = myPeopleEvalData.find(({ evaluationId }) => evalIdToStart.includes(evaluationId));
    if (startedEval) {
      window.location.href = `/users/${startedEval.userId}/evaluations/${startedEval.evaluationId}`;
    }
    yield put(setMyTeamEvalDataAction);
    yield put(displayNotification('Evaluation preparations are now in progress  🙌'));
  } catch {
    yield put(displayNotification('Failed to start the evaluation 🥺'));
  }
  yield put(setStartingEvalId(''));
}

function* setManagersIdsAsync({ payload }: ReturnType<typeof setManagersIds>) {
  yield put(setManagersLoading());
  try {
    const response: UserInfo[] = yield all(
      payload.map((id) => {
        return call(fetchUserProfilePartialData, id);
      })
    );
    yield put(setManagersData({ data: response, state: DataState.Fulfilled }));
  } catch {
    yield put(setManagersData({ data: null, state: DataState.Rejected }));
  }
}

function* setMembersIdsAsync({ payload }: ReturnType<typeof setMembersIds>) {
  yield put(setMembersLoading());
  try {
    const response: UserInfo[] = yield all(
      payload.map((id) => {
        return call(fetchUserProfilePartialData, id);
      })
    );
    yield put(setMembersData({ data: response, state: DataState.Fulfilled }));
  } catch {
    yield put(setMembersData({ data: null, state: DataState.Rejected }));
  }
}

function* watchGetMyPeopleEvalData() {
  yield takeLatest(getMyPeopleEvalData.type, getMyPeopleEvalDataAsync);
  yield takeEvery(setSelectedEvalsAction.type, setSelectedEvalsAsync);
  yield takeLatest(getAllLocationsData.type, getAllLocationsDataAsync);
  yield takeLatest(setManagersIds.type, setManagersIdsAsync);
  yield takeLatest(setMembersIds.type, setMembersIdsAsync);
}

export function* myPeopleEvalDataPageSaga(): Generator {
  yield all([watchGetMyPeopleEvalData()]);
}
